import 'src/locales/i18n';
import 'src/global.css';

import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { createRouter, RouterProvider } from '@tanstack/react-router';

import { useAuth, AuthProvider } from 'src/lib/store/auth-store';
import { getQueryClient } from 'src/lib/services/react-query.utils';
import { LegacyTenantRedirect } from 'src/components/legacy-tenant-redirect';

import { routeTree } from './routeTree.gen';

// Get the query client singleton
const queryClient = getQueryClient();

// Create the router instance
const router = createRouter({
  routeTree,
  context: {
    queryClient,
    auth: { isAuthenticated: false },
  },
  defaultPreload: 'intent',
  // Forward legacy (pre-tenancy) /dashboard/... links into the active tenant
  // prefix; show a real not-found for anything else.
  defaultNotFoundComponent: LegacyTenantRedirect,
});

// Type safety for router
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// Inner app component that has access to auth context and provides router
function InnerApp() {
  const { isAuthenticated } = useAuth();

  return (
    <RouterProvider
      router={router}
      context={{
        queryClient,
        auth: { isAuthenticated },
      }}
    />
  );
}

// Main App component with minimal providers outside router
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <InnerApp />
      </AuthProvider>
    </QueryClientProvider>
  );
}

// Mount the app
ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
